"use client";

import { Suspense, useEffect, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";

import { resolveEnvironmentName } from "@/lib/analytics/resolve-environment-name";
import { flushPendingIdentity } from "@/lib/analytics/posthog";
import { hasTrackingConsent } from "@/lib/analytics/tracking-consent";
import { scheduleIdle } from "@/lib/schedule-idle";

/**
 * O App Router não dispara pageview automático de forma confiável,
 * então a captura é manual a cada mudança de rota.
 *
 * Fica isolado num componente que não renderiza nada porque `useSearchParams`
 * obriga a existir um `<Suspense>` em volta, e tudo que estiver dentro desse
 * limite sai do HTML estático. Envolver a página inteira deixaria o Google
 * recebendo uma casca vazia.
 *
 * O `ready` entra nas dependências de propósito: como o `init` agora acontece
 * depois do load, o primeiro efeito roda com o posthog ainda descarregado. Sem
 * reagir ao `ready`, o pageview de entrada — justamente o mais importante — se
 * perderia.
 */
function PostHogPageview({ ready }: { ready: boolean }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!ready || !posthog.__loaded) return;

    const query = searchParams.toString();
    posthog.capture("$pageview", {
      $current_url: `${window.location.origin}${pathname}${query ? `?${query}` : ""}`,
    });
  }, [ready, pathname, searchParams]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!key) return;
    if (posthog.__loaded) {
      setReady(true);
      return;
    }
    if (!hasTrackingConsent()) return;

    // Telemetria pode esperar a página ficar de pé: nada aqui precisa
    // disputar a thread principal com a primeira pintura.
    return scheduleIdle(() => {
      if (posthog.__loaded) {
        setReady(true);
        return;
      }

      posthog.init(key, {
        api_host:
          process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com",
        capture_pageview: false,
        capture_pageleave: true,
        persistence: "localStorage+cookie",
        // A gravação de sessão é do LaunchDarkly (decisão do dono em
        // 2026-09-17). Com as duas ligadas, dois rrweb gravavam o mesmo DOM e o
        // `posthog-recorder.js` custava ~150ms de tarefa longa no stage.
        // Eventos, surveys e heatmaps do PostHog continuam.
        disable_session_recording: true,
      });

      // Super property: gruda em todo evento daqui pra frente, inclusive nos de autocapture.
      posthog.register({
        environment: resolveEnvironmentName(window.location.hostname),
      });

      flushPendingIdentity();
      setReady(true);
    });
  }, []);

  return (
    <>
      <Suspense fallback={null}>
        <PostHogPageview ready={ready} />
      </Suspense>
      {children}
    </>
  );
}
